import { FC, ReactNode } from 'react';
import { Dayjs } from 'dayjs';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { CalendarProps } from 'antd/lib';

import { Calendar, Empty, useWindowInnerWidth } from '~shared/ui';
import { timezoneDayjs } from '~shared/lib/utils';

import { IVoucherDetailItem } from '../../model';

interface IParentVoucherCalendarViewProps extends CalendarProps<Dayjs> {
  year: number;
  month: number;
  parentVoucherList: IVoucherDetailItem | null;
}

interface DayComponentProps {
  day: string;
  isToday: boolean;
  isDisabled?: boolean;
  isHoliday?: boolean;
  isWeekend?: boolean;
  isFeature?: boolean;
  isPresent?: boolean;
  isAbsent?: boolean;
  isIll?: boolean;
  isVacation?: boolean;
  children: ReactNode;
}

export const ParentVoucherCalendarView: FC<IParentVoucherCalendarViewProps> = ({
  year,
  month,
  parentVoucherList,
  ...props
}) => {
  const windoWidth = useWindowInnerWidth();
  const attendanceDateList = parentVoucherList?.dates;

  const DayComponent = ({
    day,
    isToday,
    isDisabled,
    isHoliday,
    isWeekend,
    isFeature,
    isPresent,
    isAbsent,
    isIll,
    isVacation,
    children,
  }: DayComponentProps) => {
    const exstraClassName = () => {
      if (isDisabled) {
        return 'hidden';
      }

      if (isToday) {
        return '!text-primary !border-primary';
      }

      if (isHoliday) {
        return '!bg-[#FF7D7D]';
      }

      if (isWeekend) {
        return '!bg-[#EAEAEA]';
      }

      if (isFeature) {
        return '!bg-white';
      }

      if (isVacation) {
        return '!bg-[#D2FEFF]';
      }

      if (isPresent) {
        return '!bg-[#D9FCE3]';
      }

      if (isAbsent) {
        return '!bg-[#F2F2F2]';
      }
    };

    return (
      <div
        className={
          exstraClassName() +
          ' grid h-[80px] bg-[#F6F6F6] p-2 w-full rounded-[4px]' +
          ' border-solid border-[1px] border-[#F0F0F0] xs:p-1 xs:h-[55px]'
        }
      >
        <p className="flex justify-end text-inherit">{day}</p>
        <div className="flex justify-center">{children}</div>
      </div>
    );
  };

  const dateCellRender = (value: Dayjs, voucher: any) => {
    if (timezoneDayjs(value).isAfter(timezoneDayjs(), 'day')) {
      return null;
    }

    ///Праздник
    if (voucher.isHoliday) {
      return (
        <div className="flex items-center justify-center w-full">
          {/* <Tooltip title={holiday[`name_${lang}`]}> */}
          <p className="text-[18px] text-white">П</p>
          {/* </Tooltip> */}
        </div>
      );
    }

    ///Выходные
    if (voucher?.id_attendance_status === 4) {
      return (
        <div className="flex items-center justify-center w-full">
          <p className="text-[18px] text-placeholder">В</p>
        </div>
      );
    }

    ///Посещал - верифицирован
    if (voucher?.id_attendance_status === 1) {
      return <CheckOutlined className="text-[18px] text-primary" />;
    }

    ///просто не посещал
    if (voucher?.id_attendance_status === 2 || !voucher?.id_attendance_status) {
      return <CloseOutlined className="!text-[17px] text-placeholder" />;
    }

    ///Не посещал по причине
    if (voucher?.id_attendance_status) {
      // const absentStatusItem = absentStatusList?.find(
      //   (a) => a?.id_attendance_status === voucher?.id_attendance_status
      // );

      return (
        <div className="flex items-center justify-center w-full">
          {/* <Tooltip title={absentStatusItem?.[`attendance_status_${lang}`]}> */}
          <p
            className={`text-[18px] whitespace-nowrap ${
              voucher?.paidable ? 'text-blue' : 'text-red'
            }`}
          >
            {voucher?.attendance_status_initial}
          </p>
          {/* </Tooltip> */}
        </div>
      );
    }
  };

  const cellRender = (value: any) => {
    const day = timezoneDayjs(value).format('D');
    const attendanceDay = attendanceDateList?.[Number(day)];
    const isCurrentMonth = value.month() === timezoneDayjs().month();

    if (!attendanceDay) {
      return null;
    }

    return (
      <>
        <DayComponent
          isToday={timezoneDayjs(value).isSame(timezoneDayjs(), 'day')}
          isDisabled={!isCurrentMonth}
          isHoliday={attendanceDay.isHoliday}
          isWeekend={attendanceDay.id_attendance_status === 4}
          isAbsent={attendanceDay.id_attendance_status === 2 || !attendanceDay.id_attendance_status}
          isIll={attendanceDay.id_attendance_status === 3}
          isVacation={attendanceDay.id_attendance_status === 6}
          isPresent={attendanceDay.id_attendance_status === 1}
          isFeature={timezoneDayjs(value).isAfter(timezoneDayjs(), 'day')}
          key={value}
          day={value.date()}
        >
          <div className="flex justify-center">{dateCellRender(value, attendanceDay)}</div>
        </DayComponent>
      </>
    );
  };

  if (!parentVoucherList) {
    return <Empty />;
  }

  return (
    <Calendar
      {...props}
      fullscreen={windoWidth > 768}
      headerRender={({}) => {
        return null;
      }}
      // locale={enUS}
      dateFullCellRender={cellRender}
      disabledDate={(date: Dayjs) => date.month() !== props.value?.month()}
      className={
        windoWidth > 768 ? 'childrenAttendanceCalendarDesktop' : 'childrenAttendanceCalendarMobile'
      }
    />
  );
};
